import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotVal = (formData.get('website_alt') as string || '').trim();

    if (honeypotVal) {
      setStatus('success');
      setEmail('');
      return;
    }

    try {
      const body = new URLSearchParams();
      formData.forEach((val, key) => {
        if (key !== 'website_alt') {
          body.append(key, val as string);
        }
      });

      const res = await fetch('https://readdy.ai/api/form/d96gkultfb6ioa7s1o5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const respText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(respText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || respText;
        setStatus('error');
        setErrorMsg(serverMsg || t('form.error'));
      }
    } catch {
      setStatus('error');
      setErrorMsg(t('form.error'));
    }
  };

  return (
    <footer className="relative w-full px-4 md:px-10 py-16 md:py-20">
      <div className="max-w-6xl mx-auto bg-foreground-900 rounded-3xl px-8 md:px-14 py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          <div className="md:col-span-5">
            <h3 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-background-50 mb-4">
              {t('footer.newsletter_title')}
            </h3>
            <p className="text-sm text-foreground-300 leading-relaxed mb-8">
              {t('footer.newsletter_desc')}
            </p>

            <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-4">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-honeypot"
              />
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer.newsletter_placeholder')}
                  className="flex-1 bg-transparent border-b border-foreground-500 px-0 py-3 text-background-50 text-sm placeholder:text-foreground-500 focus:outline-none focus:border-background-50 transition-colors"
                />
                <button
                  type="submit"
                  className="px-6 py-3 rounded-full bg-background-50 text-foreground-900 text-sm font-semibold hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  {t('footer.newsletter_btn')}
                  <i className="ri-arrow-right-line ml-2 text-sm"></i>
                </button>
              </div>
              {status === 'success' && (
                <p className="text-sm text-secondary-300">{t('form.success')}</p>
              )}
              {status === 'error' && (
                <p className="text-sm text-red-400">{errorMsg}</p>
              )}
            </form>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-sm font-semibold text-background-50 mb-5">{t('footer.guides')}</h4>
            <nav className="flex flex-col gap-3">
              <Link to="/artykul/kakobuy-sheet-poradnik" className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline whitespace-nowrap">
                {t('footer.guide1')}
              </Link>
              <Link to="/poradnik-zakupowy-kakobuy" className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline whitespace-nowrap">
                {t('footer.guide2')}
              </Link>
              <Link to="/bezpieczne-zakupy-kakobuy" className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline whitespace-nowrap">
                {t('footer.guide3')}
              </Link>
              <Link to="/artykul/kakobuy-sheet-poradnik" className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline whitespace-nowrap">
                {t('footer.guide4')}
              </Link>
            </nav>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-sm font-semibold text-background-50 mb-5">{t('footer.contact')}</h4>
            <div className="space-y-3">
              <a href="mailto:kontakt@kakobuyfinds.website" className="block text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap">
                {t('footer.email')}
              </a>
              <h4 className="text-sm font-semibold text-background-50 pt-4">{t('footer.address_label')}</h4>
              <p className="text-sm text-foreground-400 leading-relaxed">
                {t('footer.address_line1')}<br />
                {t('footer.address_line2')}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-16 pt-8 border-t border-foreground-700/50">
          <div className="flex items-center gap-6">
            <a href="https://twitter.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline whitespace-nowrap">Twitter</a>
            <a href="https://instagram.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline whitespace-nowrap">Instagram</a>
            <a href="https://facebook.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline whitespace-nowrap">Facebook</a>
          </div>
          <span className="text-sm text-foreground-500">{t('footer.copyright')}</span>
          <div className="flex items-center gap-6">
            <a href="#" className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline whitespace-nowrap">{t('footer.privacy')}</a>
            <a href="#" className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline whitespace-nowrap">{t('footer.terms')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}